import { initializeApp } from "firebase/app";
import { getMessaging, onMessage, getToken, isSupported} from "firebase/messaging";
import apiService from "../service/apiservice/apiService";
import { notification } from "antd";

class FirebaseManager {

    constructor(firebaseConfig, VAPID_KEY, email) {
        this.app = initializeApp(firebaseConfig);
        this.messaging = getMessaging(this.app);
        this.token = null;
        this.VAPID_KEY = VAPID_KEY;
        this.email = email;
        this.isTokenRefreshing = false; // Flag to prevent concurrent refresh attempts
    }

    static async isBrowserSupported() {
         return await isSupported(); 
    }

    async requestPermission() {
        try {
            const permission = await Notification.requestPermission();
            if (permission === "granted") {
                return true;
            } else {
                return false;
            }
        } catch (error) {
            console.error("Error requesting FCM permission:", error);
            return null; // Or handle the error as needed
        }
    }


    async getFirebaseToken(serviceWorkerRegistration) {
        try {
            const token = await getToken(this.messaging, { vapidKey: this.VAPID_KEY , serviceWorkerRegistration: serviceWorkerRegistration});
            this.token = token;
            this.sendTokenToServer(token);
            return token;
        } catch (error) {
            if (error.code === 'messaging/permission-blocked') {
                console.error("Notifications are blocked by the user.");
            } else if (error.code === 'messaging/unsupported-browser') {
                console.error("The current browser doesn't support notifications.");
            } else {
                console.error("Error getting FCM token:", error);
            }
            return null;
        }
    }

    registerServiceWorker(handleNotificationClick) {        
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker
              .register("/firebase-messaging-sw.js")
              .then((registration) => {
                this.getFirebaseToken(registration);
                this.listenForMessages(handleNotificationClick);
              })
              .catch((error) => {
                console.error("Service Worker registration failed:", error);
              });
        }
    }

    async sendTokenToServer(token) {
        if (!token || this.checkTokenInLocalStorage(token)) return;

        const endpoint = "event/device/v1/";
        const payload = {
            deviceId: this.email,
            airWatchUID: this.generateAirWatchUID(),
            appId: "com.niramaya",
            appVersion: "1.0.0",
            creationTimestamp: Date.now(),
            deviceModel: this.getDeviceModel(),
            fcmToken: token,
            fcmValid: true,
            os: this.detectOS(),
            osVersion: this.detectOSVersion(),
            resourceId: this.generateResourceId(),
            userName: this.email
        };

        const response = await apiService.post(endpoint, payload);
        if (response)
            this.setTokenInLocalStorage(token);
    }

    checkTokenInLocalStorage(token) {
        if (token) {
            const fcmToken = localStorage.getItem("fcmToken");
            if (fcmToken !== token) {
                return false;
            } else {
                return true;
            }
        }
        return true;
    }

    setTokenInLocalStorage(token) {
        localStorage.setItem("fcmToken", token);
    }

    listenForMessages(handleNotificationClick) {
        onMessage(this.messaging, (payload) => {
            // Display notification (or handle it in another way)
            notification.open({
                message: payload.data?.title,
                description: payload.data?.body,
                placement: "top",
                onClick: () => {
                    notification.destroy();
                    handleNotificationClick(payload.data?.click_action)
                }
            })
        });
    }

    // Function to detect OS
    detectOS() {
        const platform = navigator.platform.toLowerCase();
        if (platform.includes('win')) return 'Windows';
        if (platform.includes('mac')) return 'MacOS';
        if (platform.includes('linux')) return 'Linux';
        if (/android/.test(navigator.userAgent.toLowerCase())) return 'Android';
        if (/iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase())) return 'iOS';
        return 'Unknown';
    }

    // Function to detect OS version
    detectOSVersion() {
        return navigator.userAgent;
    }

    // Derive device model from user agent
    getDeviceModel() {
        return navigator.userAgent;
    }

    // Generate AirWatch UID
    generateAirWatchUID() {
        return 'AW-' + Math.random().toString(36).slice(2, 11);
    }

    generateResourceId() {
        return 'RS-' + Math.random().toString(36).slice(2, 11);
    }
}


export default FirebaseManager;
